import { useState, useCallback } from 'react';
import { useApi } from '../../../containers/ApiContainer';
import { useSnackbar } from '../../../containers/SnackbarContainer';
import { InboxService } from '../../../services/InboxService';

/**
 * Hook for handling queue item actions (resolve, assign)
 */
export function useQueueActions() {
  const { postgrest } = useApi();
  const snackbar = useSnackbar();
  const [isProcessing, setIsProcessing] = useState(false);

  /**
   * Resolve a quarantine item by mapping to an aircraft
   */
  const resolveQuarantine = useCallback(
    async (pendingTenantId: string, aircraftId: string) => {
      if (!postgrest) throw new Error('API not available');

      setIsProcessing(true);
      try {
        await InboxService.resolveAircraft(postgrest, pendingTenantId, aircraftId);
        snackbar.notify({
          text: 'Aircraft resolved successfully',
          severity: 'success',
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to resolve aircraft';
        snackbar.notify({
          text: message,
          severity: 'error',
        });
        throw err;
      } finally {
        setIsProcessing(false);
      }
    },
    [postgrest, snackbar]
  );

  /**
   * Assign a trip to a hangar
   */
  const assignToHangar = useCallback(
    async (tripId: string, hangarId: string) => {
      if (!postgrest) throw new Error('API not available');

      setIsProcessing(true);
      try {
        // We need to get the trip's arrival/departure dates
        // First fetch the trip
        const { data: trip, error: tripError } = await postgrest
          .from('trip')
          .select('arrival_date, departure_date')
          .eq('id', tripId)
          .single();

        if (tripError) throw new Error(tripError.message);
        if (!trip) throw new Error('Trip not found');

        await InboxService.assignToHangar(
          postgrest,
          tripId,
          hangarId,
          trip.arrival_date,
          trip.departure_date
        );

        snackbar.notify({
          text: 'Trip assigned to hangar',
          severity: 'success',
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to assign trip';
        snackbar.notify({
          text: message,
          severity: 'error',
        });
        throw err;
      } finally {
        setIsProcessing(false);
      }
    },
    [postgrest, snackbar]
  );

  return {
    resolveQuarantine,
    assignToHangar,
    isProcessing,
  };
}
